<template>
  <div class="container-fluid bank-transfers-module">
    <div class="card card-outline card-navy module-card">
      <div class="card-header module-header">
        <div class="d-flex flex-column flex-md-row align-items-md-center">
          <div class="d-flex align-items-center min-width-0">
            <span class="module-icon" aria-hidden="true">
              <i class="fas fa-exchange-alt"></i>
            </span>

            <div class="min-width-0">
              <h3 class="card-title mb-1">Traspasos bancarios</h3>
              <p class="module-description mb-0">
                Consulta los movimientos realizados entre cuentas de una misma empresa.
              </p>
            </div>
          </div>

          <div class="module-actions ml-md-auto mt-3 mt-md-0">
            <button
              v-can="'add_bank_account_transfers'"
              type="button"
              class="btn btn-navy btn-sm"
              title="Registrar nuevo traspaso"
              aria-label="Registrar nuevo traspaso"
              @click="openTransferModal"
            >
              <i class="fas fa-plus mr-1" aria-hidden="true"></i>
              Nuevo traspaso
            </button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <form
          class="filters-box mb-3"
          aria-labelledby="transfer-filters-title"
          @submit.prevent="applyFilters"
        >
          <div class="filters-header">
            <div>
              <h4 id="transfer-filters-title" class="filters-title mb-0">
                <i class="fas fa-filter mr-1" aria-hidden="true"></i>
                Filtros
              </h4>
              <small class="text-muted">Busca por empresa, cuenta, estatus o fecha.</small>
            </div>

            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="loading"
              title="Limpiar filtros"
              aria-label="Limpiar todos los filtros"
              @click="clearFilters"
            >
              <i class="fas fa-eraser mr-1" aria-hidden="true"></i>
              Limpiar
            </button>
          </div>

          <div class="row mt-3">
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <label for="filter-company" class="form-label-sm">Empresa</label>
              <select
                id="filter-company"
                v-model="filters.company_id"
                class="form-control form-control-sm"
                :disabled="loadingCatalogues"
                @change="onFilterCompanyChange"
              >
                <option value="">Todas las empresas</option>
                <option
                  v-for="company in companies"
                  :key="company.id"
                  :value="company.id"
                >
                  {{ company.name }}
                </option>
              </select>
            </div>

            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <label for="filter-account" class="form-label-sm">Cuenta</label>
              <select
                id="filter-account"
                v-model="filters.account_id"
                class="form-control form-control-sm"
                :disabled="loadingCatalogues || !filterBankAccounts.length"
              >
                <option value="">Todas las cuentas</option>
                <option
                  v-for="account in filterBankAccounts"
                  :key="accountRecordId(account)"
                  :value="accountRecordId(account)"
                >
                  {{ accountLabel(account) }}
                </option>
              </select>
            </div>

            <div class="col-12 col-md-6 col-xl-2 mb-3">
              <label for="filter-status" class="form-label-sm">Estatus</label>
              <select
                id="filter-status"
                v-model="filters.status"
                class="form-control form-control-sm"
              >
                <option value="">Todos</option>
                <option value="posted">Aplicado</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>

            <div class="col-12 col-md-6 col-xl-2 mb-3">
              <label for="filter-date-from" class="form-label-sm">Desde</label>
              <input
                id="filter-date-from"
                v-model="filters.date_from"
                type="date"
                class="form-control form-control-sm"
              />
            </div>

            <div class="col-12 col-md-6 col-xl-2 mb-3">
              <label for="filter-date-to" class="form-label-sm">Hasta</label>
              <input
                id="filter-date-to"
                v-model="filters.date_to"
                type="date"
                class="form-control form-control-sm"
                :min="filters.date_from || null"
              />
            </div>
          </div>

          <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center">
            <small v-if="invalidDateRange" class="text-danger mb-2 mb-sm-0" role="alert">
              <i class="fas fa-exclamation-circle mr-1" aria-hidden="true"></i>
              La fecha final debe ser igual o posterior a la fecha inicial.
            </small>
            <span v-else></span>

            <button
              type="submit"
              class="btn btn-outline-navy btn-sm"
              :disabled="loading || invalidDateRange"
            >
              <i
                :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"
                class="mr-1"
                aria-hidden="true"
              ></i>
              {{ loading ? 'Consultando...' : 'Consultar' }}
            </button>
          </div>
        </form>

        <section class="row indicators-row" aria-label="Resumen de traspasos">
          <div class="col-12 col-sm-6 col-xl-3">
            <div class="info-box indicator-card indicator-primary">
              <span class="info-box-icon indicator-icon bg-light-navy">
                <i class="fas fa-list" aria-hidden="true"></i>
              </span>
              <div class="info-box-content">
                <span class="info-box-text">Total general</span>
                <span class="info-box-number">{{ pagination.total }}</span>
                <small class="text-muted">Registros encontrados</small>
              </div>
            </div>
          </div>

          <div class="col-12 col-sm-6 col-xl-3">
            <div class="info-box indicator-card indicator-success">
              <span class="info-box-icon indicator-icon bg-light-success">
                <i class="fas fa-check" aria-hidden="true"></i>
              </span>
              <div class="info-box-content">
                <span class="info-box-text">Aplicados en página</span>
                <span class="info-box-number">{{ pagePostedCount }}</span>
                <small class="text-muted">De {{ transfers.length }} visibles</small>
              </div>
            </div>
          </div>

          <div class="col-12 col-sm-6 col-xl-3">
            <div class="info-box indicator-card indicator-info">
              <span class="info-box-icon indicator-icon bg-light-info">
                <i class="fas fa-university" aria-hidden="true"></i>
              </span>
              <div class="info-box-content">
                <span class="info-box-text">Cuentas en página</span>
                <span class="info-box-number">{{ pageAccountsCount }}</span>
                <small class="text-muted">Cuentas distintas involucradas</small>
              </div>
            </div>
          </div>

          <div class="col-12 col-sm-6 col-xl-3">
            <div class="info-box indicator-card indicator-danger">
              <span class="info-box-icon indicator-icon bg-light-danger">
                <i class="fas fa-ban" aria-hidden="true"></i>
              </span>
              <div class="info-box-content">
                <span class="info-box-text">Cancelados en página</span>
                <span class="info-box-number">{{ pageCancelledCount }}</span>
                <small class="text-muted">Movimientos revertidos</small>
              </div>
            </div>
          </div>
        </section>

        <div
          v-if="listError"
          class="alert alert-danger d-flex align-items-start"
          role="alert"
        >
          <i class="fas fa-exclamation-circle mt-1 mr-2" aria-hidden="true"></i>
          <div class="flex-grow-1">
            <strong>No fue posible cargar los traspasos.</strong>
            <div>{{ listError }}</div>
          </div>
          <button
            type="button"
            class="btn btn-outline-danger btn-sm ml-2"
            :disabled="loading"
            @click="loadTransfers(filters.page)"
          >
            Reintentar
          </button>
        </div>

        <div
          class="table-responsive transfers-table-wrapper"
          :aria-busy="loading ? 'true' : 'false'"
        >
          <table class="table table-sm table-hover transfers-table mb-0">
            <caption class="sr-only">
              Historial de traspasos entre cuentas bancarias
            </caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Fecha</th>
                <th scope="col">Empresa</th>
                <th scope="col">Cuenta origen</th>
                <th scope="col">Cuenta destino</th>
                <th scope="col" class="text-right">Monto</th>
                <th scope="col" class="text-center">Estatus</th>
                <th scope="col">Registró</th>
                <th scope="col" class="text-center actions-column">Acciones</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="loading">
                <td colspan="9" class="state-cell">
                  <i class="fas fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                  Cargando traspasos...
                </td>
              </tr>

              <tr v-else-if="!transfers.length">
                <td colspan="9" class="state-cell">
                  <div class="empty-state">
                    <span class="empty-state-icon" aria-hidden="true">
                      <i class="fas fa-exchange-alt"></i>
                    </span>
                    <strong>No se encontraron traspasos</strong>
                    <small>Modifica los filtros o registra un nuevo movimiento.</small>
                  </div>
                </td>
              </tr>

              <template v-else>
                <tr
                  v-for="transfer in transfers"
                  :key="transfer.id"
                  class="transfer-row"
                  :class="rowStatusClass(transfer.status)"
                >
                  <td class="text-nowrap">
                    <strong class="text-navy">{{ transfer.id }}</strong>
                  </td>
                  <td class="text-nowrap">
                    {{ formatDate(transfer.transfer_date) }}
                  </td>
                  <td>
                    <span class="cell-ellipsis" :title="companyName(transfer.company)">
                      {{ companyName(transfer.company) }}
                    </span>
                  </td>
                  <td>
                    <div class="account-cell">
                      <strong :title="accountName(transfer.source_account)">
                        {{ accountName(transfer.source_account) }}
                      </strong>
                      <small :title="accountSecondary(transfer.source_account)">
                        {{ accountSecondary(transfer.source_account) }}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div class="account-cell">
                      <strong :title="accountName(transfer.destination_account)">
                        {{ accountName(transfer.destination_account) }}
                      </strong>
                      <small :title="accountSecondary(transfer.destination_account)">
                        {{ accountSecondary(transfer.destination_account) }}
                      </small>
                    </div>
                  </td>
                  <td class="text-right text-nowrap">
                    <strong class="amount-value">{{ formatMoney(transfer.amount) }}</strong>
                  </td>
                  <td class="text-center">
                    <span class="status-pill" :class="statusClass(transfer.status)">
                      <i
                        :class="statusIcon(transfer.status)"
                        class="mr-1"
                        aria-hidden="true"
                      ></i>
                      {{ statusLabel(transfer.status) }}
                    </span>
                  </td>
                  <td>
                    <span class="cell-ellipsis" :title="userName(transfer.created_by)">
                      {{ userName(transfer.created_by) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <div class="btn-group btn-group-sm" role="group" aria-label="Acciones del traspaso">
                      <button
                        type="button"
                        class="btn btn-outline-info"
                        title="Ver detalle"
                        aria-label="Ver detalle del traspaso"
                        @click="openDetailModal(transfer)"
                      >
                        <i class="fas fa-eye" aria-hidden="true"></i>
                      </button>

                      <button
                        v-if="transfer.status === 'posted'"
                        v-can="'cancel_bank_account_transfers'"
                        type="button"
                        class="btn btn-outline-danger"
                        title="Cancelar traspaso"
                        aria-label="Cancelar traspaso"
                        @click="openCancelModal(transfer)"
                      >
                        <i class="fas fa-ban" aria-hidden="true"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div v-if="pagination.total > pagination.per_page" class="pagination-container">
          <div class="pagination-summary">
            Mostrando {{ pagination.from || 0 }}–{{ pagination.to || 0 }}
            de {{ pagination.total }} registros
          </div>

          <Pagination
            v-model="filters.page"
            :records="pagination.total"
            :per-page="pagination.per_page"
            :options="paginationOptions"
            @paginate="loadTransfers"
          />
        </div>
      </div>
    </div>

    <ModalBankAccountTransferForm />
    <ModalBankAccountTransferDetail />
    <ModalBankAccountTransferCancel />
  </div>
</template>

<script>
import Pagination from 'vue-pagination-2';
import request from '@/services/request';
import Catalogue from '@/services/catalogue';
import ModalBankAccountTransferForm from '@/components/bank_account_transfers/ModalBankAccountTransferForm.vue';
import ModalBankAccountTransferDetail from '@/components/bank_account_transfers/ModalBankAccountTransferDetail.vue';
import ModalBankAccountTransferCancel from '@/components/bank_account_transfers/ModalBankAccountTransferCancel.vue';

const EMPTY_FILTERS = () => ({
  company_id: '',
  account_id: '',
  status: '',
  date_from: '',
  date_to: '',
  page: 1,
  per_page: 20,
});

export default {
  name: 'BankAccountTransfersIndex',

  components: {
    Pagination,
    ModalBankAccountTransferForm,
    ModalBankAccountTransferDetail,
    ModalBankAccountTransferCancel,
  },

  data() {
    return {
      catalogue: new Catalogue(),
      loadingCatalogues: false,
      loading: false,
      listError: '',
      transfers: [],
      filters: EMPTY_FILTERS(),
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0,
      },
      paginationOptions: {
        chunk: 5,
        edgeNavigation: true,
        chunksNavigation: 'scroll',
        texts: {
          count: '',
          first: 'Primera',
          last: 'Última',
        },
      },
      transferRequestSequence: 0,
    };
  },

  computed: {
    companies() {
      const companies = this.catalogue.companies_all
        || this.catalogue.companies
        || [];

      return Array.isArray(companies) ? companies : [];
    },

    bankAccounts() {
      const collections = [
        this.catalogue.bank_account_all,
        this.catalogue.bank_accounts_all,
        this.catalogue.bank_accounts_companys,
      ];
      const accountsById = {};

      this.companies.forEach((company) => {
        if (company && Array.isArray(company.bank_accounts)) {
          collections.push(company.bank_accounts);
        }
      });

      collections.forEach((collection) => {
        if (!Array.isArray(collection)) {
          return;
        }

        collection.forEach((account) => {
          const accountId = this.accountRecordId(account);

          if (accountId !== null) {
            accountsById[String(accountId)] = account;
          }
        });
      });

      return Object.keys(accountsById).map((id) => accountsById[id]);
    },

    filterBankAccounts() {
      if (!this.filters.company_id) {
        return this.bankAccounts;
      }

      return this.bankAccounts.filter((account) => (
        String(this.accountCompanyId(account)) === String(this.filters.company_id)
      ));
    },

    invalidDateRange() {
      return this.filters.date_from !== ''
        && this.filters.date_to !== ''
        && this.filters.date_to < this.filters.date_from;
    },

    pagePostedCount() {
      return this.transfers.filter((transfer) => transfer.status === 'posted').length;
    },

    pageCancelledCount() {
      return this.transfers.filter((transfer) => transfer.status === 'cancelled').length;
    },

    pageAccountsCount() {
      const accountIds = [];

      this.transfers.forEach((transfer) => {
        if (transfer.source_account_id) {
          accountIds.push(String(transfer.source_account_id));
        }

        if (transfer.destination_account_id) {
          accountIds.push(String(transfer.destination_account_id));
        }
      });

      return new Set(accountIds).size;
    },
  },

  mounted() {
    this.loadCatalogues();
    this.loadTransfers(1);
  },

  created() {
    Event.$on('event-load-bank-account-transfers', this.reloadTransfers);
  },

  beforeDestroy() {
    Event.$off('event-load-bank-account-transfers', this.reloadTransfers);
  },

  methods: {
    async loadCatalogues() {
      this.loadingCatalogues = true;

      try {
        const tasks = [];

        if (typeof this.catalogue.get_companies_all === 'function') {
          tasks.push(this.catalogue.get_companies_all());
        }

        if (typeof this.catalogue.getBankAccountAll === 'function') {
          tasks.push(this.catalogue.getBankAccountAll());
        }

        await Promise.all(tasks);
      } catch (error) {
        this.listError = this.getErrorMessage(error);
      } finally {
        this.loadingCatalogues = false;
      }
    },

    async loadTransfers(page) {
      const requestSequence = this.transferRequestSequence + 1;
      this.transferRequestSequence = requestSequence;
      this.loading = true;
      this.listError = '';
      this.filters.page = Number(page || this.filters.page || 1);

      try {
        const query = this.buildQueryString({
          company_id: this.filters.company_id,
          account_id: this.filters.account_id,
          status: this.filters.status,
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
          page: this.filters.page,
          per_page: this.filters.per_page,
        });
        const response = await request.get(`/bank-account-transfers?${query}`);
        const paginator = this.extractPaginator(response);

        if (requestSequence !== this.transferRequestSequence) {
          return;
        }

        this.transfers = Array.isArray(paginator.data) ? paginator.data : [];
        this.pagination = {
          current_page: Number(paginator.current_page || 1),
          per_page: Number(paginator.per_page || this.filters.per_page),
          total: Number(paginator.total || 0),
          from: Number(paginator.from || 0),
          to: Number(paginator.to || 0),
        };
        this.filters.page = this.pagination.current_page;
      } catch (error) {
        if (requestSequence === this.transferRequestSequence) {
          this.transfers = [];
          this.listError = this.getErrorMessage(error);
        }
      } finally {
        if (requestSequence === this.transferRequestSequence) {
          this.loading = false;
        }
      }
    },

    reloadTransfers() {
      this.loadTransfers(this.filters.page);
    },

    applyFilters() {
      if (this.invalidDateRange) {
        return;
      }

      this.filters.page = 1;
      this.loadTransfers(1);
    },

    clearFilters() {
      this.filters = EMPTY_FILTERS();
      this.loadTransfers(1);
    },

    async onFilterCompanyChange() {
      this.filters.account_id = '';

      if (
        this.filters.company_id
        && typeof this.catalogue.get_bank_accounts_companys === 'function'
      ) {
        this.loadingCatalogues = true;

        try {
          await this.catalogue.get_bank_accounts_companys(this.filters.company_id);
        } catch (error) {
          this.listError = this.getErrorMessage(error);
        } finally {
          this.loadingCatalogues = false;
        }
      }
    },

    openTransferModal() {
      Event.$emit('event-show-addBankAccountTransfers', {
        company_id: this.filters.company_id || null,
      });
    },

    openDetailModal(transfer) {
      Event.$emit('event-show-showBankAccountTransferDetail', transfer);
    },

    openCancelModal(transfer) {
      Event.$emit('event-show-cancelBankAccountTransfer', transfer);
    },

    accountRecordId(account) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      return account.bank_account_company_id
        || (account.pivot && account.pivot.id)
        || account.id
        || null;
    },

    accountCompanyId(account) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      return account.company_id
        || (account.pivot && account.pivot.company_id)
        || (account.company && account.company.id)
        || null;
    },

    accountEntity(account) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      return account.bank_account
        || account.bankAccount
        || account.banks_accounts
        || account;
    },

    accountBank(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return null;
      }

      return entity.bank || entity.banks || entity.bank_data || null;
    },

    accountName(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return 'Cuenta no disponible';
      }

      const bank = this.accountBank(account);
      const accountId = this.accountRecordId(account) || entity.id || null;

      return entity.name
        || entity.account_name
        || entity.alias
        || (bank && bank.name)
        || (accountId ? `Cuenta #${accountId}` : 'Cuenta no disponible');
    },

    accountSecondary(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return '';
      }

      const bank = this.accountBank(account);
      const bankName = bank && bank.name ? bank.name : '';
      const number = entity.account_number
        || entity.number
        || account.account_number
        || account.number
        || '';

      return [bankName, number]
        .filter((value, index, values) => value && values.indexOf(value) === index)
        .join(' · ');
    },

    accountLabel(account) {
      const secondary = this.accountSecondary(account);

      return secondary
        ? `${this.accountName(account)} — ${secondary}`
        : this.accountName(account);
    },

    companyName(company) {
      if (typeof company === 'string') {
        return company;
      }

      return company && company.name ? company.name : 'Sin empresa';
    },

    userName(user) {
      if (typeof user === 'string') {
        return user;
      }

      if (!user || typeof user !== 'object') {
        return 'No disponible';
      }

      return user.name || user.full_name || user.email || 'No disponible';
    },

    statusLabel(status) {
      return status === 'cancelled' ? 'Cancelado' : 'Aplicado';
    },

    statusClass(status) {
      return status === 'cancelled'
        ? 'status-pill-cancelled'
        : 'status-pill-posted';
    },

    statusIcon(status) {
      return status === 'cancelled' ? 'fas fa-ban' : 'fas fa-check-circle';
    },

    rowStatusClass(status) {
      return status === 'cancelled'
        ? 'transfer-row-cancelled'
        : 'transfer-row-posted';
    },

    formatMoney(value) {
      const amount = Number(value || 0);
      const formatted = new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number.isFinite(amount) ? amount : 0);

      return `$ ${formatted}`;
    },

    formatDate(value) {
      if (!value) {
        return 'No disponible';
      }

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

      return Number.isNaN(date.getTime())
        ? 'No disponible'
        : date.toLocaleDateString('es-MX');
    },

    buildQueryString(params) {
      return Object.keys(params)
        .filter((key) => params[key] !== '' && params[key] !== null)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
    },

    unwrapHttpResponse(response) {
      if (
        response
        && typeof response.status === 'number'
        && Object.prototype.hasOwnProperty.call(response, 'data')
      ) {
        return response.data;
      }

      return response;
    },

    extractPaginator(response) {
      const body = this.unwrapHttpResponse(response);

      if (body && body.current_page && Array.isArray(body.data)) {
        return body;
      }

      if (
        body
        && body.data
        && body.data.current_page
        && Array.isArray(body.data.data)
      ) {
        return body.data;
      }

      return {
        current_page: 1,
        per_page: this.filters.per_page,
        total: Array.isArray(body) ? body.length : 0,
        from: Array.isArray(body) && body.length ? 1 : 0,
        to: Array.isArray(body) ? body.length : 0,
        data: Array.isArray(body) ? body : [],
      };
    },

    getErrorMessage(error) {
      const response = error && error.response ? error.response.data : null;
      const body = response || (error && error.data) || null;

      if (body && body.errors && typeof body.errors === 'object') {
        const firstKey = Object.keys(body.errors)[0];
        const firstError = firstKey ? body.errors[firstKey] : null;

        if (Array.isArray(firstError) && firstError.length) {
          return firstError[0];
        }

        if (typeof firstError === 'string') {
          return firstError;
        }
      }

      if (body && body.message) {
        return body.message;
      }

      if (error && error.message) {
        return error.message;
      }

      return 'Ocurrió un error al consultar los traspasos. Intenta nuevamente.';
    },
  },
};
</script>

<style scoped>
.bank-transfers-module {
  color: #263238;
}

.min-width-0 {
  min-width: 0;
}

.module-card {
  border-radius: 0.35rem;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.08);
  overflow: hidden;
}

.module-header {
  background-color: #fff;
  border-bottom: 1px solid #e5e9ef;
  padding: 1rem 1.25rem;
}

.module-icon {
  align-items: center;
  background-color: #e9f0f7;
  border-radius: 50%;
  color: #001f3f;
  display: inline-flex;
  flex: 0 0 auto;
  height: 42px;
  justify-content: center;
  margin-right: 0.75rem;
  width: 42px;
}

.card-title {
  color: #172033;
  font-size: 1.05rem;
  font-weight: 600;
}

.module-description {
  color: #6c757d;
  font-size: 0.78rem;
}

.filters-box {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.35rem;
  padding: 1rem;
}

.filters-header,
.pagination-container {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.filters-title {
  color: #334155;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.form-label-sm {
  color: #475569;
  font-size: 0.73rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.filters-box .form-control {
  font-size: 0.76rem;
}

.indicator-card {
  border: 1px solid #e4e9ef;
  border-left-width: 4px;
  box-shadow: none;
  min-height: 80px;
}

.indicator-primary {
  border-left-color: #001f3f;
}

.indicator-success {
  border-left-color: #28a745;
}

.indicator-info {
  border-left-color: #17a2b8;
}

.indicator-danger {
  border-left-color: #dc3545;
}

.indicator-icon {
  font-size: 1.05rem;
  width: 50px;
}

.bg-light-navy {
  background-color: #e9f0f7;
  color: #001f3f;
}

.bg-light-success {
  background-color: #eaf6ed;
  color: #218838;
}

.bg-light-info {
  background-color: #e8f5f7;
  color: #138496;
}

.bg-light-danger {
  background-color: #faeaec;
  color: #c82333;
}

.indicator-card .info-box-text {
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
}

.indicator-card .info-box-number {
  color: #172033;
  font-size: 1.08rem;
}

.indicator-card small {
  font-size: 0.68rem;
}

.transfers-table-wrapper {
  border: 1px solid #dee2e6;
  border-radius: 0.35rem;
}

.transfers-table {
  font-size: 0.75rem;
  min-width: 1080px;
}

.transfers-table thead th {
  background-color: #f4f6f9;
  border-bottom: 1px solid #d7dde4;
  color: #475569;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.025em;
  padding: 0.62rem 0.5rem;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
}

.transfers-table tbody td {
  padding: 0.55rem 0.5rem;
  vertical-align: middle;
}

.transfer-row td:first-child {
  border-left: 3px solid transparent;
}

.transfer-row-posted td:first-child {
  border-left-color: #28a745;
}

.transfer-row-cancelled {
  background-color: #fafafa;
  color: #6c757d;
}

.transfer-row-cancelled td:first-child {
  border-left-color: #dc3545;
}

.account-cell {
  display: flex;
  flex-direction: column;
  min-width: 150px;
  max-width: 220px;
}

.account-cell strong,
.account-cell small,
.cell-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-cell small {
  color: #6c757d;
  font-size: 0.68rem;
}

.cell-ellipsis {
  max-width: 170px;
}

.amount-value {
  color: #172033;
  font-size: 0.82rem;
}

.status-pill {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 50rem;
  display: inline-flex;
  font-size: 0.66rem;
  font-weight: 700;
  padding: 0.22rem 0.55rem;
  white-space: nowrap;
}

.status-pill-posted {
  background-color: #eaf6ed;
  border-color: #b9dfc2;
  color: #1f7a34;
}

.status-pill-cancelled {
  background-color: #faeaec;
  border-color: #efbdc2;
  color: #b21f2d;
}

.actions-column {
  width: 86px;
}

.state-cell {
  color: #64748b;
  padding: 2.5rem 1rem !important;
  text-align: center;
}

.empty-state {
  align-items: center;
  display: flex;
  flex-direction: column;
}

.empty-state-icon {
  align-items: center;
  background-color: #eef2f6;
  border-radius: 50%;
  color: #64748b;
  display: inline-flex;
  height: 44px;
  justify-content: center;
  margin-bottom: 0.5rem;
  width: 44px;
}

.empty-state small {
  color: #6c757d;
  margin-top: 0.15rem;
}

.pagination-container {
  border-top: 1px solid #e5e9ef;
  margin-top: 1rem;
  padding-top: 1rem;
}

.pagination-summary {
  color: #6c757d;
  font-size: 0.73rem;
}

.btn-navy {
  background-color: #001f3f;
  border-color: #001f3f;
  color: #fff;
}

.btn-navy:hover,
.btn-navy:focus {
  background-color: #00152b;
  border-color: #00152b;
  color: #fff;
}

.btn-outline-navy {
  border-color: #001f3f;
  color: #001f3f;
}

.btn-outline-navy:hover,
.btn-outline-navy:focus {
  background-color: #001f3f;
  color: #fff;
}

@media (max-width: 767.98px) {
  .card-body {
    padding: 0.75rem;
  }

  .filters-header,
  .pagination-container {
    align-items: flex-start;
    flex-direction: column;
  }

  .pagination-summary {
    margin-bottom: 0.75rem;
  }
}
</style>
